import type { FamilyFormData } from '../domain/tax/types';

export enum DataSourceType {
  PDF_IMPORTED = 'PDF_IMPORTED',
  ESTIMATED = 'ESTIMATED',
  USER_EDITED = 'USER_EDITED'
}

export interface MonthlyPayrollData {
  year: number;
  month: number;
  isBonus: boolean;
  grossSalary: number;
  healthInsurance: number;
  employmentInsurance: number;
  nationalPension: number;
  incomeTax: number;
  localTax: number;
  definedContribution: number;
  sourceType: DataSourceType;
  lastUpdated: string;
}

export interface PayrollSession {
  sessionId?: string;
  currentYear: number;
  monthlyData: MonthlyPayrollData[];
  /**
   * 扶養家族・控除の設定（UI 入力モデル）。
   * 旧データ（このフィールドを持たない localStorage / Google Drive バックアップ）を
   * 読み込んだ場合は `createFamilyFormData()` で既定値を補完する。
   */
  familyData?: FamilyFormData;
  createdAt: string;
  lastUpdated: string;
}

export interface PayrollState {
  currentSession: PayrollSession | null;
  isLoading: boolean;
  error: string | null;
}

export interface AnnualTotals {
  totalGrossSalary: number;
  totalHealthInsurance: number;
  totalEmploymentInsurance: number;
  totalNationalPension: number;
  totalIncomeTax: number;
  totalLocalTax: number;
  totalDefinedContribution: number;
}

export interface MonthlyIncomeData {
  year: number;
  month: number;
  isBonus: boolean;
  grossSalary: number;
  healthInsurance: number;
  employmentInsurance: number;
  nationalPension: number;
  definedContribution: number;
}

/**
 * PDF 解析 API（/pdf/analyze）が返す給与明細データ。
 * 解析結果は欠損し得るため任意項目とする。
 */
export interface SalarySlipData {
  salaryDate: string | null;
  isBonus?: boolean;
  grossSalary?: number;
  healthInsurance?: number;
  employmentInsurance?: number;
  nationalPension?: number;
  incomeTax?: number;
  localTax?: number;
  definedContribution?: number;
  /** 差引支給額（端末内 LLM / サーバフォールバックで抽出）。 */
  netSalary?: number;
  /** 解析信頼度（0〜1）。 */
  confidence?: number;
  /** 解析時の警告（整合性・日付不正・複数明細など）。 */
  warnings?: string[];
  /** 解析方法。旧値 "LLM" は後方互換のため許容する（サーバAI として扱う）。 */
  analysisMethod?: 'RULE' | 'BROWSER_LLM' | 'SERVER_LLM' | 'LLM';
  /** クライアント側フォールバックが必要か（低信頼かつ clientFallback=true のとき）。 */
  requiresClientFallback?: boolean;
  /** クライアントフォールバック用の PII マスク済みテキスト（不要時は null）。 */
  fallbackText?: string | null;
}